/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import { useQuery } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
import graphql from 'graphql-tag';

import { useSessionData } from '@confluence/session-data';
import { expValEquals } from '@confluence/feature-experiments';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';

import { CatchupLastViewedTimeQuery } from '../CatchupLastViewedTimeQuery.graphql';
import type {
	CatchupContentType,
	CatchupLastViewedTimeQuery as CatchupLastViewedTimeQueryResponse,
	CatchupLastViewedTimeQueryVariables,
} from '../__types__/CatchupLastViewedTimeQuery';

import type {
	useDynamicSplitButtonLastModifiedDateQuery as useDynamicSplitButtonLastModifiedDateQuery$data,
	useDynamicSplitButtonLastModifiedDateQueryVariables as useDynamicSplitButtonLastModifiedDateQuery$variables,
} from './__types__/useDynamicSplitButtonLastModifiedDateQuery';
import type {
	useDynamicSplitButtonContentHistoryQuery_Query as useDynamicSplitButtonContentHistoryQueryResponse,
	useDynamicSplitButtonContentHistoryQueryVariables,
} from './__types__/useDynamicSplitButtonContentHistoryQuery';

import { useAIRovoButtonEnablement } from '../ai-smart-button/useAIRovoButtonEnablement';

const useDynamicSplitButtonContentHistoryQuery = graphql`
	query useDynamicSplitButtonContentHistoryQuery($contentId: ID!) {
		contentHistory(contentId: $contentId, first: 1) {
			nodes {
				collaborators {
					userAccountIds
				}
			}
		}
	}
`;

type DynamicSplitButtonReturnType = {
	showCatchupAsDefault: boolean;
	queryLoading: boolean;
	queryError?: ApolloError | undefined;
};

export const useDynamicSplitButton = (
	contentId: string,
	contentType: CatchupContentType,
): DynamicSplitButtonReturnType => {
	const { isAdminHubAIEnabled, userId } = useSessionData();
	const pageMode = useGetPageMode();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();

	const {
		data,
		loading: catchupLastViewedLoading,
		error: queryError,
	} = useQuery<CatchupLastViewedTimeQueryResponse, CatchupLastViewedTimeQueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		CatchupLastViewedTimeQuery,
		{
			variables: {
				contentId,
				contentType,
			},
			skip:
				!expValEquals('cc_ai_consumption_ux_button', 'button-placement', 'bottom') ||
				!isAdminHubAIEnabled ||
				pageMode === PageMode.EDIT,
		},
	);

	const { data: lastModifiedDateData, loading: lastModifiedLoading } = useQuery<
		useDynamicSplitButtonLastModifiedDateQuery$data,
		useDynamicSplitButtonLastModifiedDateQuery$variables
	>(
		graphql`
			query useDynamicSplitButtonLastModifiedDateQuery($contentId: ID!) {
				content(id: $contentId) {
					nodes {
						id
						metadata {
							lastModifiedDate
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
			},
			skip:
				!expValEquals('cc_ai_consumption_ux_button', 'button-placement', 'bottom') ||
				!isAdminHubAIEnabled ||
				pageMode === PageMode.EDIT,
		},
	);

	const { data: contentHistoryData, loading: contentHistoryLoading } = useQuery<
		useDynamicSplitButtonContentHistoryQueryResponse,
		useDynamicSplitButtonContentHistoryQueryVariables
	>(useDynamicSplitButtonContentHistoryQuery, {
		variables: {
			contentId,
		},
		skip:
			!isAdminHubAIEnabled ||
			pageMode === PageMode.EDIT ||
			!expValEquals('cc_ai_consumption_ux_button', 'button-placement', 'bottom') ||
			!isRovoButtonEnabled,
		fetchPolicy: 'network-only',
	});

	const lastModifiedDate =
		lastModifiedDateData?.content?.nodes?.[0]?.metadata?.lastModifiedDate ?? '';
	const lastViewedTime = data?.catchupGetLastViewedTime?.lastViewedTimeISO ?? '';
	const parsedLastViewedTime = Date.parse(lastViewedTime);
	const parsedLastModifiedDate = Date.parse(lastModifiedDate);
	const lastEditedCollaborators =
		contentHistoryData?.contentHistory?.nodes?.[0]?.collaborators?.userAccountIds ?? [];
	const isCurrentUserARecentCollaborator = lastEditedCollaborators.some(
		(collaborator: string | null) => collaborator === userId,
	);

	const queryLoading = catchupLastViewedLoading || lastModifiedLoading || contentHistoryLoading;

	if (!parsedLastViewedTime || !parsedLastModifiedDate || queryError) {
		return {
			showCatchupAsDefault: false,
			queryLoading,
			queryError,
		};
	}

	const showCatchupAsDefault =
		pageMode !== PageMode.EDIT &&
		!queryLoading &&
		parsedLastModifiedDate > parsedLastViewedTime &&
		!isCurrentUserARecentCollaborator;

	return {
		showCatchupAsDefault,
		queryLoading,
	};
};
